import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, makeStyles, tokens } from '@fluentui/react-components';
import { AddFilled } from '@fluentui/react-icons';

import { useDocumentTitle } from '@shared/hooks';

import { DashboardSummaryCards, ChangeFilters, ChangesTable, TSummaryCardKey } from '../components/dashboard';
import { ChangeV2PageScaffold } from '../layouts/ChangeV2PageScaffold';
import { getChangeV2CreatePath } from '../helpers';
import { useChangeV2Store } from '../state/ChangeV2Store';
import {
  EMPTY_DASHBOARD_FILTERS,
  TDashboardFilters,
  filterChanges,
  getDashboardSummary,
  hasActiveFilters,
  sortChanges,
} from '../utils';

const useStyles = makeStyles({
  filters: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
});

const ChangesDashboardPage = () => {
  useDocumentTitle('Changes');
  const classes = useStyles();
  const navigate = useNavigate();
  const { changes, currentUser } = useChangeV2Store();

  const [filters, setFilters] = useState<TDashboardFilters>(EMPTY_DASHBOARD_FILTERS);

  const summary = useMemo(() => getDashboardSummary(changes, currentUser), [changes, currentUser]);

  const visibleChanges = useMemo(
    () => sortChanges(filterChanges(changes, filters, currentUser)),
    [changes, filters, currentUser]
  );

  const activeCardKey: TSummaryCardKey | undefined = filters.myWork
    ? 'myChanges'
    : filters.overdueOnly
      ? 'overdue'
      : filters.blockedOnly
        ? 'blocked'
        : undefined;

  const handleSelectCard = (key: TSummaryCardKey) => {
    setFilters(() => {
      const alreadyActive = activeCardKey === key;
      if (alreadyActive) return EMPTY_DASHBOARD_FILTERS;
      switch (key) {
        case 'myChanges':
        case 'myTasks':
          return { ...EMPTY_DASHBOARD_FILTERS, myWork: true };
        case 'overdue':
          return { ...EMPTY_DASHBOARD_FILTERS, overdueOnly: true };
        case 'blocked':
          return { ...EMPTY_DASHBOARD_FILTERS, blockedOnly: true };
        default:
          return EMPTY_DASHBOARD_FILTERS;
      }
    });
  };

  return (
    <ChangeV2PageScaffold>
      <Button
        appearance="primary"
        size="medium"
        icon={<AddFilled />}
        className="w-fit"
        onClick={() => navigate(getChangeV2CreatePath())}
      >
        Create change
      </Button>

      <DashboardSummaryCards summary={summary} activeKey={activeCardKey} onSelect={handleSelectCard} />

      <div className={classes.filters}>
        <ChangeFilters filters={filters} setFilters={setFilters} onClear={() => setFilters(EMPTY_DASHBOARD_FILTERS)} />
      </div>

      <ChangesTable
        changes={visibleChanges}
        hasFilters={hasActiveFilters(filters)}
        onCreate={() => navigate(getChangeV2CreatePath())}
        onClearFilters={() => setFilters(EMPTY_DASHBOARD_FILTERS)}
      />
    </ChangeV2PageScaffold>
  );
};

export default ChangesDashboardPage;
